import React, { lazy, Suspense, useEffect, useMemo } from 'react';

const APP_QUERY_PARAM = 'app';
const APP_STORAGE_KEY = 'jz_active_app';
const LEGACY_APP_KEY = 'jz9454';
const MODERN_APP_KEY = 'jz9458';
const MOBILE_MEDIA_QUERY = '(max-width: 1068px)';

const LegacyAppRoutes = lazy(() => import('./legacy/LegacyAppRoutes'));
const JZ9458App = lazy(() => import('./jz9458/components/App'));

const isValidAppKey = (value) => value === LEGACY_APP_KEY || value === MODERN_APP_KEY;

function resolveConfiguredApp() {
  const queryParams = new URLSearchParams(window.location.search);
  const queryApp = queryParams.get(APP_QUERY_PARAM);

  if (isValidAppKey(queryApp)) {
    window.localStorage.setItem(APP_STORAGE_KEY, queryApp);
    return queryApp;
  }

  const storedApp = window.localStorage.getItem(APP_STORAGE_KEY);
  if (isValidAppKey(storedApp)) {
    return storedApp;
  }

  return LEGACY_APP_KEY;
}

const App = () => {
  const activeApp = useMemo(() => {
    const isMobileViewport = window.matchMedia(MOBILE_MEDIA_QUERY).matches;
    if (isMobileViewport) {
      return MODERN_APP_KEY;
    }

    return resolveConfiguredApp();
  }, []);

  useEffect(() => {
    const mediaQuery = window.matchMedia(MOBILE_MEDIA_QUERY);
    const handleViewportChange = (event) => {
      const nextApp = event.matches ? MODERN_APP_KEY : resolveConfiguredApp();
      if (nextApp !== activeApp) {
        window.location.reload();
      }
    };

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleViewportChange);
    } else {
      mediaQuery.addListener(handleViewportChange);
    }

    return () => {
      if (typeof mediaQuery.removeEventListener === 'function') {
        mediaQuery.removeEventListener('change', handleViewportChange);
      } else {
        mediaQuery.removeListener(handleViewportChange);
      }
    };
  }, [activeApp]);

  useEffect(() => {
    // Runtime helper for quickly switching full app route trees.
    window.setJZApp = (nextApp) => {
      if (!isValidAppKey(nextApp)) return;
      window.localStorage.setItem(APP_STORAGE_KEY, nextApp);
      window.location.reload();
    };

    return () => {
      delete window.setJZApp;
    };
  }, []);

  return (
    <Suspense
      fallback={
        activeApp === MODERN_APP_KEY ? (
          <div style={{ minHeight: '100vh', background: '#000' }} />
        ) : null
      }
    >
      {activeApp === MODERN_APP_KEY ? <JZ9458App /> : <LegacyAppRoutes />}
    </Suspense>
  );
};

export default App;
